import React, { useRef, useState } from "react";
import { Box, Stack, Typography, IconButton, Tooltip, CircularProgress, Alert, Button, Link } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import dayjs from "dayjs";
import ConfirmationModal from "../../../components/confirmation-modal";
import BoBDocumentsAPI from "../../../api/BoBDocumentsAPI";
import CutoffTag from "./CutoffTag";

const C = {
	navy: "#1A3F7A",
	blue: "#3A7DC9",
	blueFaint: "#EBF5FF",
	border: "#D6E6F7",
	textSub: "#6B7FA3",
	danger: "#C62828",
};

/** Mirrors AgentBoBDocuments::MAX_FILE_BYTES; the server enforces it too. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/**
 * Mirrors FileUpload::ALLOWED_TYPES by extension, so an obvious mismatch is caught before
 * uploading. The server still checks the real bytes, which this can't.
 */
export const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "png", "jpg", "jpeg", "gif", "webp", "txt", "mp4", "webm", "mov"];

const extensionOf = (name) => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

const formatSize = (bytes) => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => (iso ? dayjs(iso).format("MMM D, YYYY h:mm A") : "");

function precheck(file) {
	if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
		return `This file type isn't supported. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}.`;
	}
	if (file.size > MAX_FILE_BYTES) {
		return `This file is larger than the ${MAX_FILE_BYTES / 1024 / 1024} MB limit.`;
	}
	return "";
}

function FileTypeIcon({ type }) {
	const sx = { fontSize: 18, color: C.blue };
	if (type === "application/pdf") return <PictureAsPdfOutlinedIcon sx={{ ...sx, color: "#C62828" }} />;
	if (type.startsWith("image/")) return <ImageOutlinedIcon sx={sx} />;
	if (type.startsWith("video/")) return <VideoFileOutlinedIcon sx={sx} />;
	if (type.includes("word") || type === "text/plain") return <DescriptionOutlinedIcon sx={sx} />;
	return <InsertDriveFileOutlinedIcon sx={sx} />;
}

function Row({ icon, title, subtitle, action, faded = false }) {
	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={1.25}
			sx={{ border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.25, py: 1, mb: 1, backgroundColor: "#fff", opacity: faded ? 0.75 : 1 }}
		>
			<Box
				sx={{
					width: 32,
					height: 32,
					borderRadius: 1,
					backgroundColor: C.blueFaint,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{icon}
			</Box>
			<Box sx={{ minWidth: 0, flex: 1 }}>
				{title}
				<Typography component="div" sx={{ fontSize: 11, color: C.textSub, lineHeight: 1.35 }}>
					{subtitle}
				</Typography>
			</Box>
			{action}
		</Stack>
	);
}

/**
 * Documents for one BoB row. Same rules as notes: the owning agent and admins can upload;
 * a document can be deleted by its uploader or an admin — the server decides, per document.
 *
 * Documents state comes from the panel (useBoBDocuments) so the header can show the count
 * while this section is collapsed.
 */
export default function ClientDocuments({ clientKey, documents, loading, error, uploadDocument, deleteDocument }) {
	const inputRef = useRef(null);
	const [pending, setPending] = useState([]);
	const [dragging, setDragging] = useState(false);
	const [pendingDelete, setPendingDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const [actionError, setActionError] = useState("");

	if (!clientKey) {
		return (
			<Typography sx={{ fontSize: 12, color: C.textSub, fontStyle: "italic", textAlign: "center", py: 2 }}>
				Documents aren't available for this record.
			</Typography>
		);
	}

	const updatePending = (key, patch) => setPending((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
	const dropPending = (key) => setPending((prev) => prev.filter((p) => p.key !== key));

	const startUploads = (fileList) => {
		const files = Array.from(fileList || []);
		if (files.length === 0) return;

		const entries = files.map((file, i) => {
			const problem = precheck(file);
			return { key: `${Date.now()}-${i}-${file.name}`, file, status: problem ? "error" : "uploading", message: problem };
		});

		setPending((prev) => [...entries, ...prev]);

		entries
			.filter((entry) => entry.status === "uploading")
			.forEach(async (entry) => {
				try {
					await uploadDocument(entry.file);
					dropPending(entry.key);
				} catch (e) {
					updatePending(entry.key, { status: "error", message: e.message });
				}
			});
	};

	const handleConfirmDelete = async () => {
		setDeleting(true);
		setActionError("");
		try {
			await deleteDocument(pendingDelete.id);
		} catch (e) {
			setActionError(e.message);
		} finally {
			setPendingDelete(null);
			setDeleting(false);
		}
	};

	const isEmpty = documents.length === 0 && pending.length === 0;

	return (
		<Box
			onDragOver={(e) => {
				e.preventDefault();
				setDragging(true);
			}}
			onDragLeave={(e) => {
				if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
			}}
			onDrop={(e) => {
				e.preventDefault();
				setDragging(false);
				startUploads(e.dataTransfer?.files);
			}}
			sx={{
				borderRadius: 1.5,
				outline: dragging ? `2px dashed ${C.blue}` : "2px dashed transparent",
				outlineOffset: 4,
				backgroundColor: dragging ? C.blueFaint : "transparent",
				transition: "all 0.15s",
			}}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
				<Typography sx={{ fontSize: 11, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5 }}>
					{documents.length} {documents.length === 1 ? "document" : "documents"}
				</Typography>
				<Button
					size="small"
					startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
					onClick={() => inputRef.current?.click()}
					sx={{
						textTransform: "none",
						fontWeight: 700,
						fontSize: 12,
						color: `${C.blue} !important`,
						backgroundColor: `${C.blueFaint} !important`,
						"&:hover": { backgroundColor: `${C.border} !important` },
					}}
				>
					Upload
				</Button>
				<input
					ref={inputRef}
					type="file"
					multiple
					hidden
					data-testid="document-input"
					accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
					onChange={(e) => {
						startUploads(e.target.files);
						// Reset so choosing the same file again still fires onChange.
						e.target.value = "";
					}}
				/>
			</Stack>

			{(error || actionError) && (
				<Alert severity="error" sx={{ mb: 1.25, py: 0 }} onClose={actionError ? () => setActionError("") : undefined}>
					{actionError || error}
				</Alert>
			)}

			{pending.map((entry) => (
				<Row
					key={entry.key}
					faded={entry.status === "uploading"}
					icon={
						entry.status === "uploading" ? (
							<CircularProgress size={16} />
						) : (
							<InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: C.danger }} />
						)
					}
					title={<Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy, wordBreak: "break-all" }}>{entry.file.name}</Typography>}
					subtitle={
						entry.status === "uploading" ? (
							"Uploading…"
						) : (
							<Box component="span" sx={{ color: C.danger }}>
								{entry.message}
							</Box>
						)
					}
					action={
						entry.status === "error" && (
							<Tooltip title="Dismiss" arrow>
								<IconButton
									size="small"
									onClick={() => dropPending(entry.key)}
									sx={{ backgroundColor: "transparent !important", color: C.textSub }}
								>
									<CloseIcon sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						)
					}
				/>
			))}

			{loading && documents.length === 0 ? (
				<Box sx={{ py: 2, textAlign: "center" }}>
					<CircularProgress size={20} />
				</Box>
			) : isEmpty && !error ? (
				<Box sx={{ py: 2, textAlign: "center" }}>
					<Typography sx={{ fontSize: 12, color: C.textSub, fontStyle: "italic" }}>No documents uploaded.</Typography>
					<Typography sx={{ fontSize: 11, color: C.textSub, mt: 0.5 }}>Drop files here or use Upload — up to 20 MB each.</Typography>
				</Box>
			) : (
				documents.map((doc) => (
					<Row
						key={doc.id}
						icon={<FileTypeIcon type={doc.file_type} />}
						title={
							<Link
								href={BoBDocumentsAPI.downloadUrl(doc.id)}
								target="_blank"
								rel="noopener noreferrer"
								underline="hover"
								sx={{ fontSize: 12.5, fontWeight: 700, color: `${C.navy} !important`, wordBreak: "break-all", display: "block" }}
							>
								{doc.file_name}
							</Link>
						}
						subtitle={
							<>
								{formatSize(doc.file_size)} · {doc.uploaded_by?.name}
								{doc.uploaded_by?.is_self ? " (You)" : ""}
								{doc.uploaded_by?.is_admin ? " · Admin" : ""} · Uploaded {formatDate(doc.created_at)}
								<CutoffTag period={doc.period} />
							</>
						}
						action={
							doc.can_delete && (
								<Tooltip title="Delete document" arrow>
									<IconButton
										size="small"
										onClick={() => setPendingDelete(doc)}
										sx={{ backgroundColor: "transparent !important", color: C.danger }}
									>
										<DeleteOutlineIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							)
						}
					/>
				))
			)}

			<ConfirmationModal
				open={!!pendingDelete}
				onClose={() => !deleting && setPendingDelete(null)}
				onConfirm={handleConfirmDelete}
				title="Delete document?"
				description={`"${pendingDelete?.file_name ?? ""}" will be permanently removed for everyone who can see this client.`}
				confirmText="Delete"
				cancelText="Cancel"
				confirmColor={C.danger}
				isLoading={deleting}
			/>
		</Box>
	);
}
